import { ReactNode } from "react";
import { Columns, Gap } from "apps/website/maps/Grid.map";

import { IconSvg } from "../../base/Icon/Icon.map";
import { IllustrationSvg } from "../../base/Illustration/illustration.map";
import { Color } from "../../base/Text/Text.map";
import { TextBodyVariantsProps } from "../../base/Text/TextBody/styles";
import { Justify } from "apps/website/maps/Flex.map";
import { Margin } from "apps/website/maps/Spacing.map";
import { TextSubtitleVariantsProps } from "../../base/Text/TextSubtitle/styles";

export interface IRadioGroupProps<T extends IBaseRadioOption> {
  name: string;
  selectedValue?: string;
  options: T[];
  legend?: string;
  srOnlyLegend?: boolean;
  onChange: (value: string) => void;
  styleOptions?: {
    columns?: Columns;
    gap?: Gap;
    gapX?: Gap;
    gapY?: Gap;
    label?: {
      justify?: Justify
      marginBottom?: Margin
    },
  };
}

// Base radio option type
export interface IBaseRadioOption<T = string> {
  label: string;
  value: T;
  component: string;
  disabled?: boolean;
  children?: ReactNode;
  childrenWrapperClassName?: string;
  selected?: boolean;
}

// Default option types
export interface IDefaultOption<T = string> extends IBaseRadioOption<T> {
  component: "default";
}

export function isRadioDefaultOption(option: IBaseRadioOption): option is IDefaultOption {
  return option.component === "default";
}

export function isRadioDefaultOptions(options: IBaseRadioOption[]): options is IDefaultOption[] {
  return options.every((option) => option.component === "default");
}

// Card option types
export interface ICardOption extends IBaseRadioOption {
  component: "card";
  icon?: IconSvg;
  selectedIcon?: IconSvg;
  description?: string;
  styleOptions?: {
    descriptionTextSize?: TextBodyVariantsProps["size"]
    selectedIcon?: {
      color?: Color;
    };
    icon?: {
      color?: Color;
    };
  };
}

export function isRadioCardOption(option: IBaseRadioOption): option is ICardOption {
  return option.component === "card";
}

export function isRadioCardOptions(options: IBaseRadioOption[]): options is ICardOption[] {
  return options.every((option) => option.component === "card");
}

// List option types
export interface IListOption<T = string> extends IBaseRadioOption<T> {
  component: "list";
  icon?: IconSvg;
  selectedIcon?: IconSvg;
  description?: string;
  styleOptions?: {
    iconPosition?: "left" | "right";
    invertIconOnSelect?: boolean;
    labelTextSize?: TextSubtitleVariantsProps["size"]
    descriptionTextSize?: TextBodyVariantsProps["size"]
  };
}

export function isRadioListOption(option: IBaseRadioOption): option is IListOption {
  return option.component === "list";
}

export function isRadioListOptions(options: IBaseRadioOption[]): options is IListOption[] {
  return options.every((option) => option.component === "list");
}

// Spotlight option types
export interface ISpotlightOption extends IBaseRadioOption {
  component: "spotlight";
  illustration: IllustrationSvg;
  /** Keeps the label for screen readers while hiding it visually. */
  hideLabel?: boolean;
}

export function isRadioSpotlightOption(option: IBaseRadioOption): option is ISpotlightOption {
  return option.component === "spotlight";
}

export function isRadioSpotlightOptions(options: IBaseRadioOption[]): options is ISpotlightOption[] {
  return options.every((option) => option.component === "spotlight");
}

// Spotlight option types
export interface IDotOption<T = string> extends IBaseRadioOption<T> {
  component: "dot";
}

export function isRadioDotOption(option: IBaseRadioOption): option is IDotOption {
  return option.component === "dot";
}

export function isRadioDotOptions(options: IBaseRadioOption[]): options is IDotOption[] {
  return options.every((option) => option.component === "dot");
}
